import { makeStyles } from '@material-ui/core';
import tw from 'twin.macro';

/** ~210px price rail at md+ (rate cards + grouped room-type header). */
const roomCardRightRailMd = {
  width: '210px',
  minWidth: '210px',
  maxWidth: '210px',
};

export default makeStyles(() => ({
  root: {
    '&': tw`flex flex-col w-full max-w-full p-0 items-stretch`,
  },
  /**
   * Mobile: full-width strip (matches group-header photo span). md+: fixed rail beside copy.
   * Without this, `maxWidth: 11rem` + `alignSelf: center` leaves large side gutters on small screens
   * (e.g. restricted/hidden rates list, which has no consensus header photo).
   */
  roomCardImageWrap: {
    position: 'relative',
    flexShrink: 0,
    alignSelf: 'stretch',
    width: '100%',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '9rem',
    '@media (min-width: 768px)': {
      alignSelf: 'stretch',
      width: '11rem',
      minWidth: '11rem',
      maxWidth: '11rem',
    },
  },
  /** Consensus photo wrapper (grid cell; md column width from template). */
  roomGroupHeaderImageWrap: {
    position: 'relative',
    width: '100%',
    display: 'block',
    flexShrink: 0,
  },
  /** Mobile: photo spans full width; md: first of three columns. */
  roomGroupHeaderPhotoSpan: {
    gridColumn: '1 / -1',
    marginBottom: 12,
    '@media (min-width: 768px)': {
      gridColumn: 'auto',
      marginBottom: 0,
    },
  },
  /** Crops photo to 4:3; positions expand control inside the image. */
  roomGroupHeaderImageInner: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 8,
    width: '100%',
    aspectRatio: '4 / 3',
    lineHeight: 0,
  },
  roomGroupHeaderImage: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  roomGroupHeaderExpandBtn: {
    position: 'absolute',
    top: 8,
    left: 8,
    zIndex: 1,
  },
  /** Grouped header grid: shared gaps and padding. */
  roomGroupHeaderGrid: {
    display: 'grid',
    alignItems: 'start',
    paddingBottom: 8,
    columnGap: 16,
    rowGap: 12,
  },
  /** Two columns: type+desc | price (no consensus photo for this group). */
  roomGroupHeaderGrid_noPhoto: {
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    '@media (min-width: 768px)': {
      gridTemplateColumns: 'minmax(0, 1fr) 210px',
    },
  },
  /** Photo spans full width on mobile; md+: photo | type+desc | price. */
  roomGroupHeaderGrid_hasPhoto: {
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    '@media (min-width: 768px)': {
      gridTemplateColumns: '10.5rem minmax(0, 1fr) 210px',
    },
  },
  /** Price column: `width: auto` on small screens so the grid stays two columns. */
  roomGroupHeaderPriceRail: {
    flexShrink: 0,
    width: 'auto',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 8,
    '@media (min-width: 768px)': roomCardRightRailMd,
  },
  /** Figma right rail: price + breakdown + primary control (~210px). */
  roomCardRightRail: {
    flexShrink: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    '@media (min-width: 768px)': roomCardRightRailMd,
  },
  /**
   * Expanded grouped rate row: grid labels | price. `width: 100%` on `roomCardRightRail`
   * collapses the second column on small screens; `auto` keeps two columns like the header rail.
   */
  roomCardRightRailInRateGrid: {
    flexShrink: 0,
    width: 'auto',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    '@media (min-width: 768px)': roomCardRightRailMd,
  },
  /** Preferred-rate pill — uses the agency's primary button colour. */
  roomCardProgramPill: {
    backgroundColor: 'rgb(var(--buttonBackground))',
    color: 'rgb(var(--textButtonBackground))',
    borderRadius: 4,
    padding: '2px 7px',
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.3,
    display: 'inline-flex',
    alignItems: 'center',
  },
  roomCardCommissionText: {
    fontSize: '0.875rem',
    color: '#595959',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  /** Grouped rate footer: centered under CTA on small screens; same as ungrouped on md+. */
  roomCardCommissionTextGroupedFooter: {
    fontSize: '0.875rem',
    color: '#595959',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    width: '100%',
    '@media (min-width: 768px)': {
      textAlign: 'right',
      width: 'auto',
    },
  },
  roomCardGuaranteeLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: 5,
    rowGap: 4,
    fontSize: '1rem',
    lineHeight: 1.25,
    width: '100%',
    marginTop: 2,
  },
  roomCardGuaranteeMuted: {
    color: '#717171',
  },
  roomCardGuaranteeBullet: {
    color: '#717171',
    fontSize: '1rem',
    lineHeight: 1,
  },
  /** Full-width segmented Add to Trip: side −/+ (flex 1∶4∶1 vs center). */
  roomCardAddTripSegmentSide: {
    display: 'flex',
    flex: '1 1 0',
    minWidth: '2.75rem',
  },
  roomCardAddTripSegmentCenter: {
    display: 'flex',
    flex: '4 1 0',
    minWidth: 0,
  },
  /** Vertical rhythm between price, breakdown link, actions in the rail. */
  roomCardPriceRailSpacerTight: {
    width: 1,
    height: 2,
    flexShrink: 0,
    display: 'block',
  },
  roomCardPriceRailSpacerLoose: {
    width: 1,
    height: 10,
    flexShrink: 0,
    display: 'block',
  },
}));
